import React from 'react';
import {
  CustomerAddress,
  SellerOrder,
  SellerOrderStatus,
} from '@shopify-multiseller/graphql/partner/types';
import { SectionCard } from '../../components/SectionCard';

const ShippingAddress: React.FC<{ address?: CustomerAddress }> = ({ address }) => {
  if (!address) return <p className="text-sm text-muted-foreground">No shipping address provided</p>;
  return (
    <p className="text-sm text-muted-foreground">
      {address.name && (
        <>
          {address.name}
          <br />
        </>
      )}
      {address.company && (
        <>
          {address.company}
          <br />
        </>
      )}
      {/* One street line. A flat or unit number is part of the address, not a
          line of its own, and splitting it made a three-line address four. */}
      {[address.address1, address.address2].filter(Boolean).join(', ')}
      <br />
      {[address.city, address.provinceCode, address.zip].filter(Boolean).join(' ')}
      <br />
      {address.country}
    </p>
  );
};

type CustomerSectionProps = {
  order?: SellerOrder;
};

/**
 * Who the order is for and where it goes.
 *
 * Three card sections with their own sub-headings became one: the name, the
 * two contact lines and the address are eight short lines in total, and
 * "Contact Information" as a heading over an email address explained nothing
 * the email address didn't.
 */
const CustomerSection: React.FC<CustomerSectionProps> = ({ order }) => {
  if (!order || !order.customer) return null;

  const { customer } = order;
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');

  // Before the seller has accepted, the order might still go back to the shop
  // — so the design shows enough to make the decision (who, and roughly where,
  // which is what shipping costs turn on) and holds the rest back until there
  // is a reason to have it. Nothing is lost: accepting reveals it, and this is
  // the one screen state where the seller cannot ship anyway.
  if (order.status === SellerOrderStatus.Pending) {
    const where = [customer.shippingAddress?.city, customer.shippingAddress?.countryCode]
      .filter(Boolean)
      .join(', ');
    return (
      <SectionCard title="Customer" testId="order-customer-section" quiet>
        <p className="text-sm text-muted-foreground">
          {[fullName, where].filter(Boolean).join(' · ')}
        </p>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Full contact details unlock after you accept.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Customer" testId="order-customer-section" quiet>
      {fullName && <p className="text-sm font-medium text-foreground">{fullName}</p>}
      <p className="mt-1 text-sm text-muted-foreground">
        {customer.email || 'No email address'}
        <br />
        {customer.phone || 'No phone number'}
      </p>
      <div className="mt-3 border-t border-border/60 pt-3">
        <p className="mb-1 text-xs font-semibold text-foreground">Ships to</p>
        <ShippingAddress address={customer.shippingAddress || undefined} />
      </div>
    </SectionCard>
  );
};

export default CustomerSection;
